/**
 * pages/Userslist.js — User management page (ADMIN only)
 * ========================================================
 * Allows an ADMIN to search for users by email and change their role.
 *
 * Features
 * ---------
 * - Search input: press Enter or clear to trigger a fetch from the backend.
 * - Results list rendered with InfiniteScroll (height-bounded, no pagination).
 * - Each list item shows:
 *     • Avatar  — initials from the first two characters of the email prefix.
 *     • Email   — truncated with ellipsis if too long.
 *     • Role tag — colour-coded badge showing current role.
 *     • Role selector — dropdown to change role (USER / FACT_CHECKER).
 *       ADMIN role is intentionally not offered as a target to prevent
 *       accidental privilege escalation via the UI.
 *
 * API calls
 * ----------
 * apiClient.getUsersList(query)      — POST /session/usrslist
 * apiClient.updateUser(addr, {role}) — POST /session/update_user
 *
 * Access control
 * --------------
 * This page is only reachable via a ProtectedRoute (see App.js).
 * The backend additionally enforces ADMIN-only access on the relevant
 * session endpoints.
 */

import { useState, useEffect } from "react";
import { useUsr } from "../App";
import { Col, Row, Input, List, Select, message, Tag, Avatar, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import apiClient from "../utils/apiClient";

const { Text } = Typography;

// Role options available in the dropdown — ADMIN is excluded intentionally
// to prevent UI-driven privilege escalation.
const ROLE_OPTIONS = [
    { label: "USER", value: "USER" },
    { label: "FACT_CHECKER", value: "FACT_CHECKER" },
];

// Ant Design tag colour map for role badges
const ROLE_COLOR = { ADMIN: "gold", FACT_CHECKER: "geekblue", USER: "cyan" };

/**
 * getInitials — derives a 2-character avatar label from an email address.
 * Takes the first two characters of the part before '@'.
 * Falls back to '?' for null/empty input.
 *
 * @param {string} email — user's email address.
 * @returns {string}     — upper-cased 2-character initials.
 */
function getInitials(email) {
    if (!email) return "?";
    return email.split("@")[0].slice(0, 2).toUpperCase();
}

export const Userslist = () => {
    const { usr } = useUsr();

    const [querystr, setQuerystr] = useState("");       // current search input value
    const [queryres, setQueryres] = useState([]);       // list of user objects from backend
    const [messageApi, messageHolder] = message.useMessage();

    /**
     * fetchUsers — calls the backend user search endpoint.
     * Called on Enter key press, on clear, and on initial mount (empty query).
     *
     * @param {string} q — search query string (partial email).
     */
    const fetchUsers = (q) => {
        if (!usr.token) return;
        apiClient.getUsersList(q)
            .then(response => {
                setQueryres(response.data.hasOwnProperty("results") ? response.data.results : []);
            })
            .catch(error => {
                console.error("Error fetching users:", error);
                messageApi.error("Failed to fetch users");
            });
    };

    // Fetch all users on mount (empty query) and whenever the query is cleared.
    // The eslint-disable is intentional — fetchUsers is stable enough that
    // it doesn't need to be in the dependency array (it reads usr.token from closure).
    useEffect(() => {
        if (querystr === "") fetchUsers("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [querystr, usr.token]);

    /**
     * updateRole — sends a partial user update to change the user's role.
     * On success, optimistically updates the local list so the badge and
     * dropdown reflect the change immediately without a full re-fetch.
     *
     * @param {string} addr — backend address of the user to update.
     * @param {string} role — new role value ('USER' | 'FACT_CHECKER').
     */
    const updateRole = (addr, role) => {
        if (!usr.token) return;
        apiClient.updateUser(addr, { role })
            .then(() => {
                // Optimistic update: replace the user's role in local state
                setQueryres(prev => prev.map(u => u.addr === addr ? { ...u, role } : u));
                messageApi.success("Role updated successfully");
            })
            .catch(error => {
                console.error("Error updating role:", error);
                messageApi.error("Failed to update user role");
            });
    };

    return (
        <div className="page-container">
            {messageHolder}

            {/* ----------------------------------------------------------------
                Search bar — centred, responsive width.
                onPressEnter and onClear both trigger a backend fetch.
            ---------------------------------------------------------------- */}
            <Row justify="center" style={{ marginBottom: 20 }}>
                <Col xs={24} sm={16} md={12}>
                    <Input
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        placeholder="Search by email…"
                        allowClear
                        value={querystr}
                        onChange={e => setQuerystr(e.target.value)}
                        onPressEnter={() => fetchUsers(querystr)}
                        onClear={() => fetchUsers("")}
                    />
                </Col>
            </Row>

            {/* Result count — only shown when there are results */}
            {queryres.length > 0 && (
                <Row justify="center" style={{ marginBottom: 8 }}>
                    <Col xs={24} sm={16} md={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {queryres.length} user{queryres.length !== 1 ? "s" : ""}
                        </Text>
                    </Col>
                </Row>
            )}

            {/* ----------------------------------------------------------------
                User list — height-bounded scroll container.
                InfiniteScroll is used for consistent scrolling behaviour;
                backend already returns up to a fixed limit so hasMore is omitted.
            ---------------------------------------------------------------- */}
            <Row justify="center">
                <Col xs={24} sm={16} md={12}>
                    <InfiniteScroll
                        style={{ height: "75vh" }}
                        dataLength={queryres.length}
                        scrollableTarget="scrollableDiv"
                    >
                        <List
                            dataSource={queryres}
                            locale={{ emptyText: <span style={{ color: "#8c8c8c", display: "block", padding: "40px 0", textAlign: "center" }}>No users found</span> }}
                            renderItem={item => (
                                <List.Item
                                    key={item.addr}
                                    style={{ padding: "10px 16px", gap: 12 }}
                                >
                                    {/* Avatar showing email initials */}
                                    <Avatar
                                        size={36}
                                        style={{ background: "#1677ff", flexShrink: 0, fontSize: 13, fontWeight: 600 }}
                                    >
                                        {getInitials(item.usr)}
                                    </Avatar>

                                    {/* Email + role badge + ADC rank */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {item.usr}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                                            <Tag
                                                color={ROLE_COLOR[item.role] || "default"}
                                                style={{ margin: 0, fontSize: 11 }}
                                            >
                                                {item.role?.replace("_", " ")}
                                            </Tag>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Rank {item.adc_rank ?? '—'}
                                            </Text>
                                        </div>
                                    </div>

                                    {/* Role change dropdown — onChange fires the backend update */}
                                    <Select
                                        size="small"
                                        defaultValue={item.role}
                                        style={{ width: 140, flexShrink: 0 }}
                                        onChange={role => updateRole(item.addr, role)}
                                        options={ROLE_OPTIONS}
                                    />
                                </List.Item>
                            )}
                        />
                    </InfiniteScroll>
                </Col>
            </Row>
        </div>
    );
};
